import { Location } from '@angular/common';
import { Component, computed, inject, OnInit, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DateTime } from 'luxon';
import { forkJoin } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { CaseTrackingService } from '../../../../service/case-tracking.service';
import { ShareLogEntry } from '../../../../types';

@Component({
  selector: 'app-case-share-history',
  templateUrl: './case-share-history.component.html',
  standalone: false,
})
export class CaseShareHistoryComponent implements OnInit {
  private route = inject(ActivatedRoute);
  private location = inject(Location);
  private caseTrackingService = inject(CaseTrackingService);

  public caseId = signal<string>('');
  public caseIndex = signal<string>('');
  public locationName = signal<string>('');
  public shareLogs = signal<ShareLogEntry[]>([]);
  public isLoading = signal<boolean>(false);
  public subheader = computed(() => {
    const trimmedLocation = this.locationName().trim();
    let header = `Case #${this.caseIndex()}`;
    if (trimmedLocation.length > 0) {
      header = `${header} - ${trimmedLocation}`;
    }
    return header;
  });

  public ngOnInit() {
    const id = this.route.snapshot.paramMap.get('caseId') ?? '';
    if (!id) return;

    this.caseId.set(id);
    this.isLoading.set(true);

    forkJoin({
      caseData: this.caseTrackingService.getCase(id),
      histories: this.caseTrackingService.getShareHistory(id),
    })
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe(
        ({ caseData, histories }) => {
          const index = caseData?.case_index;
          this.caseIndex.set(index === undefined || index === null ? '' : index.toString());
          this.locationName.set(caseData?.location_name ?? '');
          const logs = histories.items;
          this.shareLogs.set(logs ?? []);
        },
        () => this.shareLogs.set([]),
      );
  }

  public formatSentAt(iso: string): string {
    const dt = DateTime.fromISO(iso, { zone: 'utc' });
    if (!dt.isValid) {
      return iso;
    }
    return dt.toLocal().toLocaleString(DateTime.DATETIME_MED);
  }

  public back() {
    this.location.back();
  }
}
